import { useEffect, useRef } from "react"
import { getDraftAmendsByReferenceIdAndType } from "authoring/routes/project/amends/controller/get-draft-amends-by-reference-id-and-type"
import { refreshOnlyFor } from "lib/graphql/api"
import { createDraftId } from "authoring/routes/project/lib/project-utils"
import { AuthoringAmendsType } from "authoring/lib/enum/authoring-amends-type.mjs"
import { PAYLOAD_STATUS_APPROVED, PAYLOAD_STATUS_REJECTED } from "routes/audit/lib/audit-constants.mjs"
import { buildAmendsReferenceId } from "authoring/routes/project/amends/lib/amends-utils.mjs"
import { isSame } from "library/normalized-stringify/index.mjs"
import { SeedSource } from "authoring/lib/enum/seed-source.mjs"

export function useNewScheduleReviewApprovalState({
    amends,
    amendsLoading,
    projectId,
    reviewCycle,
    scheduleId,
    seedSource,
    setAcceptedById,
} = {}) {
    const initialReviewDecisionRef = useRef(false)
    const previousReviewReferenceId = getPreviousReviewReferenceId({ projectId, scheduleId, reviewCycle, seedSource })
    const { data: previousReviewAmends, loading: previousReviewAmendsLoading } =
        getDraftAmendsByReferenceIdAndType.useResults.status(
            previousReviewReferenceId,
            AuthoringAmendsType.AUTHORING_AMENDS,
            undefined,
            projectId,
            refreshOnlyFor(createDraftId(projectId, scheduleId))
        ) ?? {}

    useEffect(
        () =>
            handleSetInitialReviewDecision({
                amends,
                amendsLoading,
                initialReviewDecisionRef,
                previousReviewAmends,
                previousReviewAmendsLoading,
                seedSource,
                setAcceptedById,
            }),
        [amends, amendsLoading, previousReviewAmends, previousReviewAmendsLoading, seedSource, setAcceptedById]
    )
}

function buildInitialReviewDecisionById({ currentCycleAmends = [], previousReviewAmends = [] }) {
    const initialReviewDecisionById = {}
    const currentCycleAmendsByKey = getLatestAmendByReviewKey(currentCycleAmends)
    const previousReviewAmendsByKey = getLatestAmendByReviewKey(previousReviewAmends)

    for (const [reviewKey, previousAmend] of previousReviewAmendsByKey.entries()) {
        const status = previousAmend?.payload?.status
        const decisionValue = getDecisionValueFromStatus(status)
        if (decisionValue == null) continue

        const currentAmend = currentCycleAmendsByKey.get(reviewKey)
        if (!currentAmend) continue
        if (!isSame(currentAmend?.payload?.original, previousAmend?.payload?.original, { invalidChars: [] })) continue
        if (!isSame(currentAmend?.payload?.tailored, previousAmend?.payload?.tailored, { invalidChars: [] })) continue

        const id = currentAmend?.id
        if (!reviewKey || initialReviewDecisionById[reviewKey]) continue
        initialReviewDecisionById[reviewKey] = {
            accepted: decisionValue,
            auditId: id,
        }
    }

    return initialReviewDecisionById
}

function getDecisionValueFromStatus(status) {
    if (status === PAYLOAD_STATUS_APPROVED) return true
    if (status === PAYLOAD_STATUS_REJECTED) return false
    return null
}

function getLatestAmendByReviewKey(amends) {
    const latestAmendByReviewKey = new Map()
    for (const amend of amends) {
        const reviewKey = amend?.payload?.taskTitle ?? amend?.payload?.taskId ?? null
        if (reviewKey && !latestAmendByReviewKey.has(reviewKey)) {
            latestAmendByReviewKey.set(reviewKey, amend)
        }
        const amendsKey = amend?.payload?.amendsKey
        if (amendsKey && !latestAmendByReviewKey.has(amendsKey)) {
            latestAmendByReviewKey.set(amendsKey, amend)
        }
    }
    return latestAmendByReviewKey
}

function getPreviousReviewReferenceId({ projectId, scheduleId, reviewCycle, seedSource }) {
    if (seedSource !== SeedSource.NEW) return null
    if (!projectId || !scheduleId || reviewCycle == null) return null
    const previousReviewCycle = reviewCycle - 1
    if (previousReviewCycle < 1) return null
    return buildAmendsReferenceId({ id: createDraftId(projectId, scheduleId), reviewCycle: previousReviewCycle })
}

function handleSetInitialReviewDecision({
    amends,
    amendsLoading,
    initialReviewDecisionRef,
    previousReviewAmends,
    previousReviewAmendsLoading,
    seedSource,
    setAcceptedById,
}) {
    if (seedSource !== SeedSource.NEW) return
    if (initialReviewDecisionRef.current) return
    if (amendsLoading || previousReviewAmendsLoading) return

    setAcceptedById(
        buildInitialReviewDecisionById({
            currentCycleAmends: amends ?? [],
            previousReviewAmends: previousReviewAmends ?? [],
        })
    )
    initialReviewDecisionRef.current = true
}
